import { Injectable, inject, signal } from '@angular/core';

import { Clock, DsLoggingService, FeedName } from '@cubigo/digital-signage';

import { FeedRequestHealthModel } from 'app/shared/models';

@Injectable({ providedIn: 'root' })
export class FeedHealthService {
  private readonly logging = inject(DsLoggingService);
  private readonly clock = inject(Clock);
  private readonly feedState = signal<Partial<Record<FeedName, FeedRequestHealthModel>>>({});

  public readonly feeds = this.feedState.asReadonly();

  public record(url: string, success: boolean): void {
    const feed = this.classify(url);

    if (!feed) {
      return;
    }

    const previous = this.feeds()[feed];
    const health: FeedRequestHealthModel = {
      lastSuccessAt: success ? this.clock.now().toEpochMilli() : previous?.lastSuccessAt ?? null,
      consecutiveFailures: success ? 0 : (previous?.consecutiveFailures ?? 0) + 1
    };
    this.feedState.update(feeds => ({ ...feeds, [feed]: health }));
    this.logging.trackEvent(success ? 'feed_succeeded' : 'feed_failed', {
      feed, consecutiveFailures: health.consecutiveFailures
    });
  }

  private classify(url: string): FeedName | null {
    const parsed = new URL(url);
    const path = parsed.pathname.toLowerCase();

    if (path.endsWith('/newsitems')) {
      return parsed.searchParams.get('channelId') === '-1' ? FeedName.Birthdays : FeedName.News;
    }

    if (path.endsWith('/currentprioritynews')) {
      return FeedName.PriorityNews;
    }

    if (path.includes('/syndication/activities/')) {
      return FeedName.Activities;
    }

    if (path.includes('/syndication/youtubeactivities/')) {
      return FeedName.ScheduledVideo;
    }

    if (path.includes('/syndication/dining/')) {
      return path.endsWith('/locations') ? FeedName.DiningLocations : FeedName.Dining;
    }

    const suffixes: ReadonlyArray<readonly [string, FeedName]> = [
      ['/getcommunityweather', FeedName.Weather], ['/syndication/mail', FeedName.Mail],
      ['/syndicatorconfiguration', FeedName.Configuration], ['/branding/community', FeedName.Branding],
      ['/servicenames', FeedName.ServiceNames], ['/youtubeinfo', FeedName.YoutubeLive]
    ];

    return suffixes.find(([suffix]) => path.endsWith(suffix))?.[1] ?? null;
  }
}
